import { ApolloClient, ApolloLink, HttpLink, InMemoryCache } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { Auth0Client } from '@auth0/auth0-spa-js';
import { inflate } from 'graphql-deduplicator';

import { relayStylePagination } from '@apollo/client/utilities';
import introspectionResult from './graphql/introspection-result.json';

export const getClient = async (auth0Client: Auth0Client) => {
  // Get the API URL from the env vars
  let API = process.env.REACT_APP_API_URL;

  // Define the GraphQL API endpoint
  const httpLink = new HttpLink({ uri: API });

  // Handle errors
  const errorLink = onError(({ graphQLErrors, networkError }) => {
    // Error handling
  });

  // Fetch the token on every request — Auth0 SDK handles caching and silent refresh internally
  const withTokenLink = setContext(async () => {
    const bearerToken = await auth0Client.getTokenSilently();
    return { bearerToken };
  });

  // Apollo link to add the bearer token to the headers
  const authLink = setContext((_, { headers, bearerToken }) => {
    return {
      headers: {
        ...headers,
        ...(bearerToken ? { Authorization: `Bearer ${bearerToken}` } : {}),
      },
    };
  });

  const inflateLink = new ApolloLink((operation, forward) => {
    return forward(operation).map((response) => {
      return inflate(response);
    });
  });

  /* Create Apollo Link array to pass to Apollo Client */
  const link = ApolloLink.from([withTokenLink, authLink, errorLink, inflateLink, httpLink]);

  // Define the in memory cache
  const cache = new InMemoryCache({
    ...introspectionResult,
    typePolicies: {
      Query: {
        fields: {
          internalRegulations: relayStylePagination(['input']),
          internalRegulationObligations: relayStylePagination(['input']),
          internalRegulationObligationRules: relayStylePagination(['input']),
          internalRegulationObligationsByDocument: relayStylePagination(['input']),
        },
      },
    },
  });

  // Setup apollo client with the link and cache
  const client = new ApolloClient({
    link,
    cache,
  });

  // Need to disable cache? Commenting out persistCache below
  // Persist the cache to local storage
  // await persistCache({ cache, storage: window.localStorage });

  return client;
};
